import React, { useMemo, useState } from "react";
import { useGetMyWallet, type Transaction } from "@workspace/api-client-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusBadge } from "@/components/StatusBadge";
import { formatZAR } from "@/lib/formatters";
import { Download, Loader2, Wallet, ArrowDownLeft, Repeat, Banknote, ChevronDown } from "lucide-react";

// Returns an array of YYYY-MM strings from the oldest transaction month up to
// the current month, most recent first.
function buildPeriodOptions(txDates: (string | null | undefined)[]): string[] {
  const now = new Date();
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const months = new Set<string>([current]);
  for (const d of txDates) {
    if (!d) continue;
    const dt = new Date(d);
    months.add(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
  }
  return Array.from(months).sort().reverse();
}

const TYPE_META: Record<
  string,
  { label: string; icon: React.ComponentType<{ className?: string }> }
> = {
  once_off_connection_fee: { label: "Connection fee", icon: ArrowDownLeft },
  recurring_commission: { label: "Recurring commission", icon: Repeat },
  payout: { label: "Payout", icon: Banknote },
};

export function WalletView() {
  const { data: wallet, isLoading, error } = useGetMyWallet();
  const [exporting, setExporting] = useState(false);
  const now = new Date();
  const currentPeriod = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [selectedPeriod, setSelectedPeriod] = useState(currentPeriod);

  const periodOptions = useMemo(
    () => buildPeriodOptions(wallet?.transactions.map((t) => t.createdAt) ?? []),
    [wallet],
  );

  const downloadStatement = async () => {
    setExporting(true);
    try {
      const url = `/api/wallet/statement/export?period=${encodeURIComponent(selectedPeriod)}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objUrl;
      a.download = `mooya-statement-${selectedPeriod}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(objUrl);
    } finally {
      setExporting(false);
    }
  };

  const totals = useMemo(() => {
    const txns = wallet?.transactions ?? [];
    const sumBy = (type: string) =>
      txns
        .filter((t) => t.type === type)
        .reduce((acc, t) => acc + t.amountCents, 0);
    return {
      connectionFees: sumBy("once_off_connection_fee"),
      recurring: sumBy("recurring_commission"),
      payouts: sumBy("payout"),
    };
  }, [wallet]);

  if (isLoading)
    return (
      <div className="p-6 animate-pulse">
        <div className="h-32 bg-gray-200 rounded mb-4"></div>
        <div className="h-64 bg-gray-200 rounded"></div>
      </div>
    );
  if (error || !wallet)
    return <div className="p-6 text-red-500">Failed to load wallet.</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-[#0B132B]">Wallet</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={selectedPeriod}
              onChange={(e) => setSelectedPeriod(e.target.value)}
              className="appearance-none border border-gray-200 rounded-lg text-sm px-3 py-1.5 pr-7 bg-white text-[#0B132B] focus:outline-none focus:ring-2 focus:ring-[#E6007E]/30"
            >
              {periodOptions.map((p) => {
                const [y, m] = p.split("-");
                const label = new Date(Number(y), Number(m) - 1).toLocaleDateString("en-ZA", {
                  month: "short",
                  year: "numeric",
                });
                return (
                  <option key={p} value={p}>
                    {label}
                  </option>
                );
              })}
            </select>
            <ChevronDown className="w-3 h-3 text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={downloadStatement}
            disabled={exporting}
            className="border-gray-200"
          >
            {exporting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
            ) : (
              <Download className="w-3.5 h-3.5 mr-1.5" />
            )}
            Download
          </Button>
        </div>
      </div>

      <Card className="bg-[#0B132B] text-white border-0 overflow-hidden relative">
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -mr-10 -mt-10 blur-2xl"></div>
        <CardContent className="p-6 relative z-10 flex flex-col gap-2">
          <p className="text-sm text-gray-300 flex items-center gap-2">
            <Wallet className="w-4 h-4" /> Available Balance
          </p>
          <p className="text-4xl font-bold">{formatZAR(wallet.balanceCents)}</p>
          <div className="flex gap-6 mt-4">
            <div>
              <p className="text-xs text-gray-400">Pending</p>
              <p className="font-semibold">{formatZAR(wallet.pendingCents)}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Held</p>
              <p className="font-semibold text-amber-400">
                {formatZAR(wallet.heldCents)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-3">
        <EarningCard
          label="Connection fees"
          value={formatZAR(totals.connectionFees)}
          icon={ArrowDownLeft}
        />
        <EarningCard
          label="Recurring commission"
          value={formatZAR(totals.recurring)}
          icon={Repeat}
        />
        <EarningCard
          label="Paid out"
          value={formatZAR(totals.payouts)}
          icon={Banknote}
          className="col-span-2"
        />
      </div>

      <div>
        <h2 className="text-lg font-bold text-[#0B132B] mb-4">
          Recent Transactions
        </h2>
        <div className="flex flex-col gap-3">
          {wallet.transactions.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No transactions found.
            </div>
          ) : (
            wallet.transactions.map((tx) => (
              <TransactionRow key={tx.id} tx={tx} />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function EarningCard({
  label,
  value,
  icon: Icon,
  className = "",
}: {
  label: string;
  value: string;
  icon: React.ComponentType<{ className?: string }>;
  className?: string;
}) {
  return (
    <Card className={`border-gray-100 shadow-sm rounded-xl ${className}`}>
      <CardContent className="p-4">
        <div className="w-8 h-8 rounded-full bg-[#00A859]/10 flex items-center justify-center mb-3">
          <Icon className="w-4 h-4 text-[#00A859]" />
        </div>
        <p className="text-xs text-gray-500 font-medium mb-1">{label}</p>
        <p className="text-xl font-bold text-[#0B132B]">{value}</p>
      </CardContent>
    </Card>
  );
}

function TransactionRow({ tx }: { tx: Transaction }) {
  const meta = TYPE_META[tx.type] ?? {
    label: tx.type.replace(/_/g, " "),
    icon: Banknote,
  };
  const Icon = meta.icon;
  return (
    <Card className="border-gray-100 shadow-sm">
      <CardContent className="p-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center shrink-0">
            <Icon className="w-4 h-4 text-[#0B132B]" />
          </div>
          <div>
            <p className="font-medium text-[#0B132B]">{meta.label}</p>
            <p className="text-xs text-gray-500">
              {tx.createdAt
                ? new Date(tx.createdAt).toLocaleDateString()
                : ""}{" "}
              • {tx.reference}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-bold text-[#0B132B]">{formatZAR(tx.amountCents)}</p>
          <StatusBadge status={tx.status} />
        </div>
      </CardContent>
    </Card>
  );
}
